import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { useGetPayablesLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";

export interface UseDuplicateInvoiceWarningParams {
  arenaId?: string | null;
  payeeId?: string | null;
  payeeType?: string | null;
  invoiceNumber?: string | null;
  /** Debounce window in milliseconds. Default: 500. */
  debounceMs?: number;
}

export interface UseDuplicateInvoiceWarningResult {
  hasDuplicate: boolean;
  loading: boolean;
}

const VENDOR_PAYEE_TYPE = "vendor";

/**
 * Returns `hasDuplicate=true` when the (arena, payee, invoiceNumber) tuple
 * matches an existing payable on the server. The check is debounced and only
 * runs for vendor payees with all three values present.
 *
 * Relies on the server-side `payeeIds` + `invoiceNumberExact` filters so the
 * decision is authoritative without client-side pagination.
 */
export const useDuplicateInvoiceWarning = ({
  arenaId,
  payeeId,
  payeeType,
  invoiceNumber,
  debounceMs = 500,
}: UseDuplicateInvoiceWarningParams): UseDuplicateInvoiceWarningResult => {
  const trimmedInvoice = invoiceNumber?.trim() ?? "";
  const isVendor = payeeType?.toLowerCase() === VENDOR_PAYEE_TYPE;
  const isReady = isVendor && !!arenaId && !!payeeId && trimmedInvoice.length > 0;

  const currentKey = isReady ? `${arenaId}|${payeeId}|${trimmedInvoice}` : "";
  const latestKeyRef = useRef<string>("");
  const isMountedRef = useRef<boolean>(true);

  const [hasDuplicate, setHasDuplicate] = useState(false);
  const [loading, setLoading] = useState(false);

  const [getPayables] = useGetPayablesLazyQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const runQuery = useCallback(
    async (key: string, arena: string, payee: string, invoice: string) => {
      // Apollo's useLazyQuery execute resolves (with `error` populated) rather
      // than rejecting for GraphQL or network errors, so `res.data` is simply
      // undefined and the count falls back to 0 — we don't need a catch.
      const res = await getPayables({
        variables: {
          first: 1,
          filters: {
            arenaIds: [arena],
            payeeIds: [payee],
            invoiceNumberExact: invoice,
          },
        },
      });
      if (!isMountedRef.current || latestKeyRef.current !== key) {
        return;
      }
      setHasDuplicate((res.data?.payables?.count ?? 0) > 0);
      setLoading(false);
    },
    [getPayables],
  );

  const debounced = useMemo(() => debounce(runQuery, debounceMs), [runQuery, debounceMs]);

  useEffect(() => {
    latestKeyRef.current = currentKey;
    setHasDuplicate(false);
    if (!isReady) {
      debounced.cancel();
      setLoading(false);
      return;
    }
    setLoading(true);
    void debounced(currentKey, arenaId, payeeId, trimmedInvoice);
    return () => {
      debounced.cancel();
    };
  }, [currentKey, isReady, arenaId, payeeId, trimmedInvoice, debounced]);

  return { hasDuplicate, loading };
};
